import { darkTheme, lightTheme } from '@controlrooms/design-tokens';

export const annotationTheme = Object.fromEntries(
  Object.entries({ light: lightTheme, dark: darkTheme }).map(([key, theme]) => [
    key,
    {
      // Panel Container
      panelBackground: theme.color.surface.cool[2], // #b8c4c6
      panelBorder: theme.color.surface[5], // #b7b5b1
      panelShadow: 'rgba(0, 0, 0, 0.2)',

      // Header
      headerBackground: theme.color.surface.cool[2], // #d6dcdd
      headerShadow: 'rgba(0, 0, 0, 0.2)',
      headerTitle: theme.color.text.core['-primary'], // #1e1e1e
      headerIcon: theme.color.text.core['-primary'], // #1e1e1e
      closeButton: theme.color.text.core['-primary'], // #1e1e1e
      closeButtonHover: theme.color.text.core['-secondary'], // #737373

      // Body
      bodyBackground: theme.color.surface.cool[2], // #b8c4c6
      subheadText: theme.color.text.core['-primary'], // #1e1e1e
      subheadIcon: theme.color.text.core['-secondary'], // #737373

      // Notes List
      noteBackground: theme.color.surface[1], // #f7f7f5
      noteBorder: theme.color.surface[3], // #d9d7d4
      noteAuthorText: theme.color.text.core['-emphasis'], // #000000
      noteTimestampText: theme.color.text.core['-tertiary'], // #a6a6a6
      noteContentText: theme.color.text.core['-primary'], // #1e1e1e
      noteLocationText: theme.color.text.core['-primary'], // #1e1e1e
      noteTagBackground: theme.color.dataVis.qualitative['blue-2'], // #5ba0cb
      noteTagText: theme.color.text.core['on-dark-primary'], // #ffffff
      noteTagBackgroundAlt: theme.color.dataVis.qualitative['magenta-2'], // #cb5b83
      flagTextColor: theme.color.text.core['on-light-primary'],

      // Form Section
      formBackground: theme.color.surface.cool[2], // #d6dcdd
      formBorder: theme.color.surface[0], // #b8c4c6
      formShadow: 'rgba(0, 0, 0, 0.15)',

      // Input Fields
      inputBackground: theme.color.surface[2], // #f2f2f2
      inputBorder: theme.color.surface[3], // #d9d7d4
      inputText: theme.color.text.core['-primary'], // #1e1e1e
      inputPlaceholder: theme.color.text.core['-tertiary'],

      // Control Panel
      controlPanelBackground: theme.color.surface.cool[2], // #d6dcdd
      controlPanelBorder: theme.color.surface[3], // #d9d7d4
      controlButtonBackground: theme.color.surface[1], // #f7f7f5
      controlButtonBorder: theme.color.surface[3], // #d9d7d4
      controlButtonText: theme.color.text.core['-primary'], // #1e1e1e
      controlButtonIcon: theme.color.text.core['-primary'], // #1e1e1e
      controlButtonHoverBackground: theme.color.surface[2], // #f2f2f2
      controlButtonHoverBorder: theme.color.surface[4], // #c4c2bf

      // Search
      searchInputBackground: theme.color.surface[1], // #f7f7f5
      searchInputBorder: theme.color.surface[3], // #d9d7d4
      searchInputText: theme.color.text.core['-primary'], // #1e1e1e
      searchInputPlaceholder: theme.color.text.core['-tertiary'], // #a6a6a6
      searchInputFocusBorder: theme.color.dataVis.qualitative['blue-2'], // #5ba0cb
      searchButtonColor: theme.color.text.core['-primary'], // #1e1e1e
      searchButtonHoverBackground: theme.color.surface[2], // #f2f2f2
      searchButtonHoverColor: theme.color.text.core['-secondary'], // #737373 // #a6a6a6
      inputShadow: 'rgba(0, 0, 0, 0.1)',

      // Dropdown
      dropdownBackground: theme.color.surface[2], // #f2f2f2
      dropdownBorder: theme.color.surface[3], // #d9d7d4
      dropdownText: theme.color.text.core['-primary'], // #1e1e1e
      dropdownIcon: theme.color.text.core['-secondary'], // #737373

      // Buttons
      buttonBackground: theme.color.surface[1], // #f9f9f9
      buttonBorder: theme.color.surface.cool[2], // #d6dcdd
      buttonText: theme.color.text.core['-primary'], // #1e1e1e
      buttonTextDisabled: theme.color.text.core['-tertiary'], // #a6a6a6
      buttonShadow: 'rgba(0, 0, 0, 0.2)',
      buttonHoverBackground: theme.color.surface[0], // #ffffff

      // Checkbox
      checkboxBackground: theme.color.surface[1], // #f7f7f5
      checkboxBorder: theme.color.surface[3], // #d9d7d4
      checkboxCheckedBackground: theme.color.focus[3], // #7199a1
      checkboxAccent: theme.color.focus[3], // #7199a1

      // Input Labels
      inputLabelText: theme.color.text.core['-primary'], // #1e1e1e
      inputDescriptionText: theme.color.text.core['-tertiary'], // #a6a6a6

      // Scrollbar
      scrollbarBackground: 'rgba(255, 255, 255, 0.75)',
      scrollbarGrabber: theme.color.focus[3], // #7199a1
      scrollbarBorder: theme.color.surface[1], // #f7f7f5

      // Timeframe Section
      timeframeBackground: theme.color.surface.cool[2], // #d6dcdd
      timeframeBorder: theme.color.surface.cool[2], // #b8c4c6
      timeframeText: theme.color.text.core['-primary'], // #1e1e1e
      timeframeLabel: theme.color.text.core['-primary'], // #1e1e1e

      // Legacy support (keeping existing properties)
      activeBackground: theme.color.focus[3],
      activeBorder: theme.color.focus[1],
      activeText: theme.color.surface[0],
      activeHoverBackground: theme.color.focus[3],
      activeIcon: theme.color.surface[0],
      inactiveBorder: theme.color.focus[1],
      inactiveText: theme.color.text.core['-secondary'],
      inactiveHoverBackground: theme.color.surface[1],
      inactiveIcon: theme.color.text.core['-secondary'],
      popoverBackground: theme.color.surface[0],
      popoverBorder: theme.color.surface[2],
      popoverShadow: 'rgba(0, 0, 0, 0.15)',
      popoverArrow: theme.color.surface[0],
      popoverTitle: theme.color.text.core['-primary'],
      remarksText: theme.color.text.core['-primary'],
      remarkTimeText: theme.color.text.core['-secondary'],
      chevronIcon: theme.color.text.core['-secondary'],
      remarkContentText: theme.color.text.core['-primary'],
      textareaBorder: theme.color.focus[1],
      textareaBackground: theme.color.surface[1],
      textareaText: theme.color.focus[1],
      textareaFocusBorder: theme.color.focus[1],
      bubbleBackground: theme.color.surface[0],
      bubbleBorder: theme.color.surface[3],
      bubbleHoverBackground: theme.color.focus[6],
      bubbleHoverBorder: theme.color.focus[4],
      bubbleText: theme.color.text.core['-primary'],
      bubbleShadow: theme.color.surface[4],
      // New bubble dot styles
      bubbleDotFill: theme.color.dataVis.qualitative['teal-1'], // #B1F6D9
      bubbleDotStroke: theme.color.dataVis.core['data-primary'], // #1e1e1e (light) / #d9d9d9 (dark)
      bubbleDotOuterBorder: theme.color.surface[3], // #a0a38d
      tooltipBackground: theme.color.surface[2],
      tooltipBorder: theme.color.focus[1],
      panelHeaderBorder: theme.color.focus[1],
      panelTitle: theme.color.focus[1],
      itemBackground: theme.color.surface[1],
      itemBorder: theme.color.focus[1],
      timeText: theme.color.focus[1],
      actionButton: theme.color.focus[1],
      actionButtonHover: theme.color.focus[1],
      text: theme.color.focus[1],
      emptyStateText: theme.color.focus[1],
    },
  ]),
);
